import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Button,
  Chip,
  Skeleton,
  useMediaQuery,
  useTheme,
  Paper,
  Stack,
  Divider,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import {
  ArrowBack,
  Palette,
  Brush,
  ShoppingCart,
  Favorite,
  FavoriteBorder
} from '@mui/icons-material';
import QuoteForm from '../components/QuoteForm';
import { publicUploadUrl } from '../utils/mediaUrls';

// Sample t-shirt designs data
const TSHIRT_DESIGNS = {
  football: [
    {
      id: 1,
      name: "Classic Football Kit",
      description: "Traditional football jersey with modern fit",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
      price: 29.99,
      colors: ["Red", "Blue", "Green", "Yellow", "Black", "White"],
      sizes: ["XS", "S", "M", "L", "XL", "XXL"],
      category: "football",
      featured: true
    },
    {
      id: 2,
      name: "Modern Striker Kit",
      description: "Contemporary design with performance features",
      image: "https://images.unsplash.com/photo-1552318965-6e6be7484ada?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
      price: 34.99,
      colors: ["Navy", "Red", "White", "Orange"],
      sizes: ["S", "M", "L", "XL", "XXL"],
      category: "football",
      featured: false
    },
    {
      id: 3,
      name: "Vintage Football Jersey",
      description: "Retro-inspired design with classic appeal",
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
      price: 27.99,
      colors: ["Cream", "Brown", "Green", "Blue"],
      sizes: ["M", "L", "XL"],
      category: "football",
      featured: false
    }
  ],
  basketball: [
    {
      id: 4,
      name: "Pro Basketball Jersey",
      description: "Professional basketball uniform design",
      image: "https://images.unsplash.com/photo-1546519638-68e109498ffc?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
      price: 32.99,
      colors: ["Red", "Black", "White", "Gold"],
      sizes: ["S", "M", "L", "XL", "XXL"],
      category: "basketball",
      featured: true
    },
    {
      id: 5,
      name: "Street Basketball Kit",
      description: "Urban-inspired basketball design",
      image: "https://images.unsplash.com/photo-1574629812330-259f87c107c6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
      price: 29.99,
      colors: ["Black", "Grey", "Red", "Blue"],
      sizes: ["M", "L", "XL", "XXL"],
      category: "basketball",
      featured: false
    }
  ],
  cricket: [
    {
      id: 6,
      name: "Traditional Cricket Kit",
      description: "Classic cricket uniform design",
      image: "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
      price: 31.99,
      colors: ["Green", "White", "Gold", "Navy"],
      sizes: ["S", "M", "L", "XL"],
      category: "cricket",
      featured: true
    }
  ],
  hockey: [
    {
      id: 7,
      name: "Ice Hockey Jersey",
      description: "Professional hockey uniform",
      image: "https://images.unsplash.com/photo-1551698618-1dfe5d97d256?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
      price: 35.99,
      colors: ["Red", "Black", "White", "Blue"],
      sizes: ["M", "L", "XL", "XXL"],
      category: "hockey",
      featured: true
    }
  ]
};

const CATEGORY_INFO = {
  football: {
    name: "Football Kits",
    description: "Custom football jerseys and kits for your team",
    icon: "⚽"
  },
  basketball: {
    name: "Basketball Uniforms",
    description: "Professional basketball jerseys and uniforms",
    icon: "🏀"
  },
  cricket: {
    name: "Cricket Kits",
    description: "Traditional cricket uniforms and jerseys",
    icon: "🏏"
  },
  hockey: {
    name: "Hockey Jerseys",
    description: "Ice hockey uniforms and jerseys",
    icon: "🏒"
  }
};

function TshirtSelection() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const category = slug || searchParams.get('category') || 'football';
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [selectedColors, setSelectedColors] = useState({});
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [quoteFormOpen, setQuoteFormOpen] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchDesigns = async () => {
      setLoading(true);
      
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "/api";
        const response = await axios.get(`${apiUrl}/posts/category/${category}`);
        
        // Transform API data to match component expectations
        const transformedDesigns = response.data.map(post => {
          const image = post.featured_image_name
            ? publicUploadUrl(post.featured_image_name)
            : 'https://via.placeholder.com/300x300?text=No+Image';

          return {
            id: post.id,
            name: post.title,
            description: post.description || 'Custom t-shirt design',
            image,
            price: post.price || 29.99, // Use price from API or default
            colors: ["Red", "Blue", "Green", "Yellow", "Black", "White"], // Default colors
            sizes: ["XS", "S", "M", "L", "XL", "XXL"], // Default sizes
            category: category,
            featured: post.status === 'published'
          };
        });
        
        setDesigns(transformedDesigns);
      } catch (error) {
        // Do not show sample designs when live category images cannot be loaded.
        setDesigns([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDesigns();
  }, [category]);

  const handleFavorite = (designId) => {
    setFavorites(prev => 
      prev.includes(designId) 
        ? prev.filter(id => id !== designId)
        : [...prev, designId]
    );
  };

  const handleDesignSelect = (design) => {
    navigate('/customize', {
      state: { design, category } 
    });
  };

  const renderBreadcrumbs = () => (
    <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
      <MuiLink component={Link} to="/" color="inherit">
        Home
      </MuiLink>
      <Typography color="text.primary">
        {CATEGORY_INFO[category]?.name || 'Category'}
      </Typography>
    </Breadcrumbs>
  );

  const renderCategoryHeader = () => (
    <Box sx={{ mb: 4, textAlign: 'center' }}>
      <Typography variant="h1" component="h1" gutterBottom sx={{ fontSize: isMobile ? '2.5rem' : '3.5rem' }}>
        {CATEGORY_INFO[category]?.icon} {CATEGORY_INFO[category]?.name}
      </Typography>
      <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
        {CATEGORY_INFO[category]?.description}
      </Typography>
      <Button
        component={Link}
        to="/"
        startIcon={<ArrowBack />}
        variant="outlined"
        size="large"
      >
        Back to Categories
      </Button>
    </Box>
  );

  const renderDesignCard = (design) => (
    <Grid item xs={12} sm={6} md={4} key={design.id}>
      <Card 
        sx={{ 
          height: '100%',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4
          }
        }}
      >
        <CardActionArea 
          onClick={() => handleDesignSelect(design)}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <Box sx={{ position: 'relative' }}>
            <CardMedia
              component="img"
              height="300"
              image={design.image}
              alt={design.name}
              sx={{ objectFit: 'cover' }}
            />
            {design.featured && (
              <Chip
                label="Featured"
                color="primary"
                size="small"
                sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  fontWeight: 'bold'
                }}
              />
            )}
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleFavorite(design.id);
              }}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                minWidth: 'auto',
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 1)'
                }
              }}
            >
              {favorites.includes(design.id) ? (
                <Favorite color="error" />
              ) : (
                <FavoriteBorder />
              )}
            </Button>
          </Box>
          <CardContent sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="h3" gutterBottom>
              {design.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {design.description}
            </Typography>
            <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
              ${design.price}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Available Colors:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {design.colors.map((color, index) => (
                  <Chip 
                    key={index} 
                    label={color} 
                    size="small" 
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Available Sizes:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {design.sizes.map((size, index) => (
                  <Chip 
                    key={index} 
                    label={size} 
                    size="small" 
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
            
            <Box sx={{ p: 2, pt: 0 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<ShoppingCart />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleGetQuote(design);
                }}
                sx={{ mb: 1 }}
              >
                Get Quote
              </Button>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );

  const renderDesigns = () => (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
        Available Designs
      </Typography>
      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item}>
              <Card sx={{ height: '100%' }}>
                <Skeleton variant="rectangular" height={300} />
                <CardContent>
                  <Skeleton variant="text" height={32} sx={{ mb: 1 }} />
                  <Skeleton variant="text" height={20} sx={{ mb: 2 }} />
                  <Skeleton variant="text" height={24} sx={{ mb: 2 }} />
                  <Skeleton variant="text" height={20} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={3}>
          {designs.map(renderDesignCard)}
        </Grid>
      )}
    </Box>
  );

  const renderCTASection = () => (
    <Box
      sx={{
        bgcolor: 'grey.100',
        py: 6,
        borderRadius: 2,
        textAlign: 'center'
      }}
    >
      <Container maxWidth="md">
        <Typography variant="h4" component="h2" gutterBottom>
          Can't Find What You're Looking For?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          We can create custom designs just for your team. Contact us for special requests.
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
          <Button 
            component={Link} 
            to="/contact" 
            variant="contained" 
            size="large"
            startIcon={<Brush />}
          >
            Request Custom Design
          </Button>
          <Button 
            component={Link} 
            to="/" 
            variant="outlined" 
            size="large"
            startIcon={<Palette />}
          >
            Browse Other Categories
          </Button>
        </Stack>
      </Container>
    </Box>
  );

  if (!CATEGORY_INFO[category]) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Category Not Found
        </Typography>
        <Button component={Link} to="/" variant="contained">
          Back to Home
        </Button>
      </Container>
    );
  }

  const handleGetQuote = (design) => {
    setSelectedDesign(design);
    setSelectedColors({ primary: '#1976d2', secondary: '#dc004e' });
    setSelectedSizes(['M', 'L']);
    setQuantities({ M: 1, L: 1 });
    setQuoteFormOpen(true);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {renderBreadcrumbs()}
      {renderCategoryHeader()}
      {renderDesigns()}
      {renderCTASection()}
      
      <QuoteForm
        open={quoteFormOpen}
        onClose={() => setQuoteFormOpen(false)}
        designData={selectedDesign}
        selectedColors={selectedColors}
        selectedSizes={selectedSizes}
        quantities={quantities}
      />
    </Container>
  );
}

export default TshirtSelection;
